import React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export interface SearchPanelProps {
    /** Current search query */
    searchQuery: string;
    /** Called when search query changes */
    onSearchQueryChange: (query: string) => void;
    /** Called when search is submitted */
    onSearch: () => void;
    /** Called when search is cleared */
    onClear: () => void;
    /** Whether search is currently loading */
    searchLoading: boolean;
    /** Number of results to display in chip (optional) */
    resultsCount?: number;
    /** Whether the panel is expanded */
    expanded: boolean;
    /** Called when expanded state changes */
    onExpandedChange: (expanded: boolean) => void;
}

/**
 * Collapsible search panel: the query input for the keyword search over sections.
 */
export function SearchPanel({
    searchQuery,
    onSearchQueryChange,
    onSearch,
    onClear,
    searchLoading,
    resultsCount,
    expanded,
    onExpandedChange,
}: SearchPanelProps) {
    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            onSearch();
        }
    };

    return (
        <Card sx={{mb: 2}}>
            <Box
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    cursor: 'pointer',
                    transition: 'background-color 0.15s cubic-bezier(0.25, 1, 0.5, 1)',
                    '&:hover': {bgcolor: 'action.hover'},
                }}
                onClick={() => onExpandedChange(!expanded)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onExpandedChange(!expanded);
                    }
                }}
            >
                <SearchIcon aria-hidden="true" sx={{mr: 1, color: 'text.secondary'}}/>
                <Typography variant="body1" sx={{flex: 1}}>
                    Search Regulations
                </Typography>
                {resultsCount !== undefined && resultsCount > 0 && (
                    <Chip
                        label={`${resultsCount} results`}
                        size="small"
                        color="primary"
                        sx={{mr: 1}}
                    />
                )}
                <IconButton size="small" tabIndex={-1} aria-hidden="true">
                    {expanded ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                </IconButton>
            </Box>
            <Collapse in={expanded}>
                <CardContent sx={{pt: 0}}>
                    <Box sx={{display: 'flex', gap: 2, alignItems: 'flex-start'}}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search (e.g., 'byggehøyde', 'brannsikkerhet', 'ventilasjon')..."
                            value={searchQuery}
                            onChange={(e) => onSearchQueryChange(e.target.value)}
                            onKeyDown={handleKeyPress}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small"/>
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={onClear} aria-label="Clear search">
                                                <ClearIcon fontSize="small"/>
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onSearch}
                            disabled={searchLoading || !searchQuery.trim()}
                            sx={{minWidth: 80}}
                        >
                            {searchLoading ? <CircularProgress size={20}/> : 'Search'}
                        </Button>
                    </Box>
                </CardContent>
            </Collapse>
        </Card>
    );
}

export default SearchPanel;
